import { apiService } from '../../../services/apiService';
import {
  API_PATHS,
  getAcquirerSoftDeletePath,
  getAcquirerStatusPath,
} from '../../../utils/apiPaths';
import { createCompleteGridFetcher } from '../../../utils/fetchCompleteGridList';
import type { AcquirerListRequest } from '../models/types';
import { buildAcquirersRequest, normalizeAcquirer } from '../utils/utils';

export async function fetchAcquirersList(payload: AcquirerListRequest) {
  const res: any = await apiService.post(API_PATHS.GET_ACQUIRERS, payload);
  if (!res?.success) {
    throw new Error('Failed to fetch acquirers');
  }
  const pageData = res?.data?.content ? res.data : res;
  const pageMeta =
    pageData.page && typeof pageData.page === 'object' && !Array.isArray(pageData.page)
      ? pageData.page
      : null;
  const rows = (pageData.content || []).map((item: any) => normalizeAcquirer(item));
  const reportedTotal = pageData.totalElements ?? pageMeta?.totalElements ?? rows.length;
  const totalElements = Number(reportedTotal);
  return {
    rows,
    totalElements: Number.isFinite(totalElements) ? totalElements : rows.length,
  };
}

export const fetchAcquirersGrid = createCompleteGridFetcher((
  options: Parameters<typeof buildAcquirersRequest>[0],
) => fetchAcquirersList(buildAcquirersRequest(options)));

export async function deleteAcquirer(id: number) {
  await apiService.patch(getAcquirerSoftDeletePath(id));
}

export async function updateAcquirerStatus(id: number, status: string) {
  await apiService.patch(getAcquirerStatusPath(id), null, {
    params: { status },
  });
}

export async function fetchAcquirerDropdownOptions() {
  const [methodsRes, countriesRes]: any[] = await Promise.all([
    apiService.get('/getActivePaymentMethods'),
    apiService.get(API_PATHS.GET_ACTIVE_COUNTRIES),
  ]);

  const methodsRaw = Array.isArray(methodsRes?.data)
    ? methodsRes.data
    : Array.isArray(methodsRes)
      ? methodsRes
      : [];

  const paymentMethodOptions = methodsRaw
    .map((m: any) => {
      const autoId = Number(m.autoId ?? m.id ?? 0);
      const paymentMethod = String(
        m.paymentMethod ?? m.name ?? m.type ?? m.method ?? m.label ?? '',
      ).trim();
      return { autoId, paymentMethod };
    })
    .filter((m: { autoId: number; paymentMethod: string }) => m.autoId > 0 && m.paymentMethod.length > 0);

  const countriesRaw = Array.isArray(countriesRes?.data)
    ? countriesRes.data
    : Array.isArray(countriesRes)
      ? countriesRes
      : [];

  const countries = countriesRaw
    .map((c: any) => ({
      autoId: Number(c.autoId ?? c.id ?? 0),
      countryName: String(c.countryName ?? c.name ?? '').trim(),
      countryFlag: String(c.countryFlag ?? '').trim().toUpperCase(),
    }))
    .filter(
      (c: { autoId: number; countryName: string; countryFlag: string }) =>
        c.autoId > 0 && c.countryName.length > 0 && c.countryFlag.length === 2,
    );

  return { paymentMethodOptions, countries };
}

export async function fetchCountryCurrencyLookups(): Promise<
  Array<{ autoId: number; currency: string }>
> {
  const countriesRes: any = await apiService.get(API_PATHS.GET_ACTIVE_COUNTRIES);
  const countriesRaw = Array.isArray(countriesRes?.data)
    ? countriesRes.data
    : Array.isArray(countriesRes)
      ? countriesRes
      : [];

  return countriesRaw
    .map((c: any) => ({
      autoId: Number(c.autoId ?? c.id ?? 0),
      currency: String(c.currency ?? '').trim().toUpperCase(),
    }))
    .filter(
      (c: { autoId: number; currency: string }) => c.autoId > 0 && c.currency.length > 0,
    );
}

export async function fetchAcquirerById(acqId: string) {
  const res: any = await apiService.get(`/acquirers/${acqId}`);
  if (!res.success) {
    throw new Error('Acquirer not found');
  }
  return res.data;
}

export async function createAcquirer(payload: Record<string, unknown>) {
  await apiService.post('/addAcquirer', payload);
}

export async function updateAcquirer(payload: Record<string, unknown>) {
  await apiService.post('/updateAcquirer', payload);
}
